<template>
  <!-- Main Content -->
  <div class="d-flex align-center">
    <!-- Section tabs -->
    <v-tabs
      v-model="currentTab"
      color="primary"
      class="flex-grow-1"
      v-if="!selectedPlacementId"
    >
      <v-tab
        value="placements"
        aria-label="Placements tab"
        data-testid="placements-tab"
        tabindex="0"
      >
        <v-icon start class="mr-2">mdi-view-dashboard</v-icon>
        <span class="d-block">Placements</span>
      </v-tab>
      <v-tab
        value="inventory"
        aria-label="Inventory tab"
        data-testid="inventory-tab"
      >
        <v-icon start class="mr-2">mdi-view-module</v-icon>
        <span class="d-block">Inventory</span>
      </v-tab>
      <v-tab value="trends" aria-label="Trends tab" data-testid="trends-tab">
        <v-icon start class="mr-2">mdi-chart-areaspline</v-icon>
        <span class="d-block">Trends</span>
      </v-tab>
      <v-tab value="help" aria-label="Help tab" data-testid="help-tab">
        <v-icon start class="mr-2">mdi-help-circle</v-icon>
        <span class="d-block">Help</span>
      </v-tab>
    </v-tabs>
  </div>

  <!-- Filters -->
  <div class="mt-4 d-flex pa-2 align-start justify-start" v-if="showFilters">
    <!-- Compact Placement Filter -->
    <div
      class="filters-container"
      v-if="!selectedPlacementId && availablePlacements.length > 0"
    >
      <!-- Filter Icon with Badge -->
      <v-menu
        v-model="placementMenu"
        :close-on-content-click="false"
        data-testid="placement-filter-menu"
      >
        <template v-slot:activator="{ props }">
          <div
            class="d-flex align-center"
            style="cursor: pointer"
            v-bind="props"
            data-testid="placement-filter-btn"
          >
            <v-icon
              icon="mdi-filter-variant"
              size="small"
              color="grey-darken-1"
              class="mr-1"
            />
            <span class="text-caption text-grey-darken-1 mr-2">Placements</span>
            <!-- Filter Status Badge -->
            <v-chip
              v-if="filteredPlacements.length < availablePlacements.length"
              size="x-small"
              color="primary"
              variant="flat"
              class="text-caption"
            >
              {{ filteredPlacements.length }}/{{ availablePlacements.length }}
            </v-chip>
          </div>
        </template>

        <v-card min-width="280" class="pa-3">
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="text-subtitle-2 font-weight-medium"
              >Filter Placements</span
            >
            <div class="d-flex gap-1">
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="selectAllPlacements"
                class="text-caption"
              >
                All
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="grey"
                @click="clearAllPlacements"
                class="text-caption"
              >
                Clear
              </v-btn>
              <v-btn
                data-testid="close-filter-btn"
                size="x-small"
                variant="text"
                color="grey"
                @click="placementMenu = false"
                class="text-caption"
              >
                Close
              </v-btn>
            </div>
          </div>

          <!-- Placement Checkboxes -->
          <div class="overflow-y-auto" style="max-height: 200px">
            <v-checkbox
              v-for="placement in availablePlacements"
              :key="placement"
              v-model="filteredPlacements"
              :value="placement"
              :label="getPlacementDisplayName(placement)"
              hide-details
              density="compact"
              class="mb-1"
            />
          </div>

          <!-- Active Filters Summary -->
          <div
            v-if="filteredPlacements.length < availablePlacements.length"
            class="mt-3 pt-2"
            style="border-top: 1px solid #e0e0e0"
          >
            <div class="d-flex flex-wrap gap-1">
              <v-chip
                v-for="placement in filteredPlacements"
                :key="placement"
                size="x-small"
                variant="outlined"
                color="primary"
                closable
                @click:close="removePlacementFilter(placement)"
                class="text-caption"
              >
                {{ placement }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-menu>

      <!-- Device Type Filter Menu - Only show on Placements and Inventory tabs -->
      <v-menu
        v-model="deviceTypeMenu"
        :close-on-content-click="false"
        data-testid="device-type-filter-menu"
        v-if="
          (currentTab === 'placements' || currentTab === 'inventory') &&
          !selectedPlacementId
        "
      >
        <template v-slot:activator="{ props }">
          <div
            class="d-flex align-center"
            style="cursor: pointer"
            v-bind="props"
            data-testid="device-type-filter-btn"
          >
            <v-icon
              icon="mdi-devices"
              size="small"
              color="grey-darken-1"
              class="mr-1"
            />
            <span class="text-caption text-grey-darken-1 mr-2"
              >Device Types</span
            >
            <!-- Filter Status Badge -->
            <v-chip
              v-if="filteredDeviceTypes.length < 4"
              size="x-small"
              color="primary"
              variant="flat"
              class="text-caption"
            >
              {{ filteredDeviceTypes.length }}/4
            </v-chip>
          </div>
        </template>

        <v-card min-width="280" class="pa-3">
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="text-subtitle-2 font-weight-medium"
              >Filter Device Types</span
            >
            <div class="d-flex gap-1">
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="selectAllDeviceTypes"
                class="text-caption"
              >
                All
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="grey"
                @click="clearAllDeviceTypes"
                class="text-caption"
              >
                Clear
              </v-btn>
            </div>
          </div>

          <!-- Device Type Checkboxes -->
          <div class="overflow-y-auto" style="max-height: 200px">
            <v-checkbox
              v-for="deviceType in availableDeviceTypes"
              :key="deviceType"
              v-model="filteredDeviceTypes"
              :value="deviceType"
              :label="deviceType"
              hide-details
              density="compact"
              class="mb-1"
            />
          </div>

          <!-- Active Filters Summary -->
          <div
            v-if="filteredDeviceTypes.length < 4"
            class="mt-3 pt-2"
            style="border-top: 1px solid #e0e0e0"
          >
            <div class="d-flex flex-wrap gap-1">
              <v-chip
                v-for="deviceType in filteredDeviceTypes"
                :key="deviceType"
                size="x-small"
                variant="outlined"
                color="primary"
                closable
                @click:close="removeDeviceTypeFilter(deviceType)"
                class="text-caption"
              >
                {{ deviceType }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-menu>

      <!-- Date Range Pickers -->
      <div class="d-flex align-center" v-if="showDateRangePickers">
        <DateRangePicker
          v-model:start-date="startDate"
          v-model:end-date="endDate"
          :style="{ backgroundColor: '#e9f0fd' }"
          class="date-range-picker"
        />
        <v-btn
          v-show="!showCompareDates"
          variant="plain"
          class="mx-2 pa-0"
          size="small"
          @click="handleShowCompareDates"
          append-icon="mdi-chevron-right"
          data-testid="compare-dates-btn"
        >
          compare
        </v-btn>
        <div class="d-flex align-center ml-2" v-if="showCompareDates">
          <DateRangePicker
            v-model:start-date="previousStartDate"
            v-model:end-date="previousEndDate"
            :max-date="maxPreviousEndDate"
            :style="{ backgroundColor: '#fcefd8' }"
            class="compare-date-range-picker"
          />
        </div>
        <v-btn
          v-show="showCompareDates"
          variant="plain"
          class="mx-2 pa-0"
          size="small"
          @click="handleHideCompareDates"
          prepend-icon="mdi-chevron-left"
          data-testid="hide-compare-dates-btn"
        />
      </div>
    </div>
  </div>

  <!-- Dashboards -->
  <v-window v-model="currentTab" :touch="false">
    <!-- Placement dashboard -->
    <v-window-item value="placements">
      <v-card>
        <Placements
          v-if="selectedPlacementId == null"
          :account-id="accountId"
          :account-name="accountName"
          :placements="filteredPlacementsData"
          :placementsCount="String(filteredPlacements.length)"
          :start-date="startDate"
          :end-date="endDate"
          :previous-start-date="previousStartDate"
          :previous-end-date="previousEndDate"
          :is-ready="isInitialized"
          :device-type-filters="filteredDeviceTypes"
          :has-edit-access="hasEditAccess"
          @update:placementId="selectedPlacementId = $event"
          @update:deviceTypeFilters="filteredDeviceTypes = $event"
          @addPlacement="showAddPlacementDialog = true"
        />
        <v-container v-if="selectedPlacementId != null">
          <PlacementSettingsManager
            :account-name="accountId"
            :placements="placements"
            :placement-id="selectedPlacementId"
            :cdn="cdn"
            @update:placementId="selectedPlacementId = $event"
          />
        </v-container>
      </v-card>
    </v-window-item>

    <!-- Vehicles dashboard -->
    <v-window-item value="inventory">
      <Vehicles
        :account-id="accountId"
        :account-name="accountName"
        :start-date="startDate"
        :end-date="endDate"
        :previous-start-date="previousStartDate"
        :previous-end-date="previousEndDate"
        :placements="filteredPlacements"
        :is-ready="isInitialized"
        :device-type-filters="filteredDeviceTypes"
        @update:deviceTypeFilters="filteredDeviceTypes = $event"
      />
    </v-window-item>

    <!-- Trends dashboard -->
    <v-window-item value="trends">
      <Trends
        :account-id="accountId"
        :account-name="accountName"
        :start-date="startDate"
        :end-date="endDate"
        :previous-start-date="previousStartDate"
        :previous-end-date="previousEndDate"
        :placements="filteredPlacements"
      />
    </v-window-item>

    <!-- Help content -->
    <v-window-item value="help">
      <Help />
    </v-window-item>
  </v-window>

  <!-- Add Placement Dialog -->
  <v-dialog
    v-model="showAddPlacementDialog"
    max-width="500px"
    data-testid="add-placement-dialog"
    v-if="hasEditAccess"
  >
    <v-card>
      <v-card-title>Add New Placement</v-card-title>
      <v-card-text>
        <v-text-field
          data-testid="placement-id-input"
          v-model="newPlacementId"
          label="Placement ID"
          hint="Enter a unique identifier for the placement"
          persistent-hint
          variant="outlined"
          density="comfortable"
          class="mb-4"
          hide-details
          required
        ></v-text-field>
        <v-text-field
          data-testid="placement-url-input"
          v-model="newPlacementUrl"
          label="Placement URL"
          hint="Enter the URL where this placement will be displayed"
          placeholder="https://example.com/vehicles"
          persistent-hint
          variant="outlined"
          density="comfortable"
          class="mb-4"
          hide-details
          required
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="closeAddPlacementDialog">
          Cancel
        </v-btn>
        <v-btn
          data-testid="add-placement-submit-btn"
          color="primary"
          @click="addNewPlacement"
          :disabled="!newPlacementId || !newPlacementUrl || !!placementIdError"
        >
          Add Placement
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar -->
  <v-snackbar
    v-model="showSnackbar"
    :timeout="2000"
    :color="snackbarColor"
    data-testid="success-snackbar"
  >
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useProductSettingsStore } from '@/stores/productSettings'
import Placements from '@/components/dashboard/Placements.vue'
import Vehicles from '@/components/dashboard/Vehicles.vue'
import DateRangePicker from '@/components/dashboard/components/DateRangePicker.vue'
import PlacementSettingsManager from '@/components/PlacementSettingsManager.vue'
import Trends from '@/components/dashboard/Trends.vue'
import Help from '@/components/Help.vue'

import {
  getDefaultPlacementSettings,
  getFirstDayOfCurrentMonthAsDate,
  getFirstDayOfLastMonthAsDate,
  getLastDayOfCurrentMonthAsDate,
  getLastDayOfLastMonthAsDate,
} from '@/stores/utils'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { formatPlacementIdToFriendlyName } from '@/utils'
import type { IPlacements } from '@/types'
import { config } from '@/config/generated-config'

const cdn: string = config.VITE_CDN_URL ?? ''
const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const isInitialized = ref(false)

const accountId = computed(() => {
  return accountSettings.accountId
})
const accountName = computed(() => {
  return accountSettings.accountName
})

const newPlacementId = ref('')
const newPlacementUrl = ref('')
const filteredPlacements = ref<string[]>([])
const filteredDeviceTypes = ref<string[]>([
  'mobile',
  'desktop',
  'tablet',
  'unknown',
])
const selectedPlacementId = ref<string | null>(null)
const selectedPlacementFriendlyName = ref<string>('')
const placementMenu = ref(false)
const deviceTypeMenu = ref(false)
const showAddPlacementDialog = ref(false)

const currentTab = ref('placements')

const startDate = ref(getFirstDayOfCurrentMonthAsDate())
const endDate = ref(getLastDayOfCurrentMonthAsDate())
const previousStartDate = ref<Date | null>(null)
const previousEndDate = ref<Date | null>(null)
const showCompareDates = ref(false)

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const availablePlacements = computed(() => {
  // Build array of IDs that are not preview entries, then sort by friendly name
  const ids = Object.keys(productSettings?.placements)
    .map(key => {
      if (key.startsWith('preview__')) return null
      return key
    })
    .filter((item): item is string => item !== null)

  return ids.sort((a, b) => {
    const pa = productSettings?.placements[a]
    const pb = productSettings?.placements[b]
    const nameA = pa?.friendlyName || formatPlacementIdToFriendlyName(a)
    const nameB = pb?.friendlyName || formatPlacementIdToFriendlyName(b)
    return nameA.localeCompare(nameB, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  })
})

const filteredPlacementsData = computed(() => {
  if (!filteredPlacements.value || filteredPlacements.value.length === 0) {
    return productSettings?.placements
  }

  return Object.keys(productSettings?.placements)
    .filter(key => filteredPlacements.value.includes(key))
    .reduce((acc, key) => {
      acc[key] = productSettings?.placements[key]
      return acc
    }, {} as IPlacements)
})

// Helper function to get placement display name with status
const getPlacementDisplayName = (placementId: string) => {
  const placement = productSettings?.placements[placementId]
  const friendly =
    placement?.friendlyName || formatPlacementIdToFriendlyName(placementId)
  if (placement && placement.enabled === false) {
    return `${friendly} (Disabled)`
  }
  return friendly
}

const availableDeviceTypes = computed(() => {
  return ['mobile', 'desktop', 'tablet', 'unknown']
})

const showFilters = computed(() => {
  return currentTab.value !== 'help' && !selectedPlacementId.value
})

const maxPreviousEndDate = computed(() => {
  const dayBeforeStart = new Date(startDate.value)
  dayBeforeStart.setDate(dayBeforeStart.getDate() - 1)
  return dayBeforeStart
})

const showDateRangePickers = computed(() => {
  return (
    availablePlacements.value.length > 0 &&
    ((currentTab.value !== 'help' &&
      currentTab.value !== 'trends' &&
      selectedPlacementId.value == null) ||
      currentTab.value === 'inventory')
  )
})

// Edit access control (centralized via store helper)
const hasEditAccess = computed(() => accountSettings.hasEditAccess)

const closeAddPlacementDialog = () => {
  newPlacementId.value = ''
  newPlacementUrl.value = ''
  showAddPlacementDialog.value = false
}

const addNewPlacement = () => {
  if (!newPlacementId.value || !newPlacementUrl.value || placementIdError.value)
    return

  const newSettings: IPlacements = {
    [newPlacementId.value]: {
      ...getDefaultPlacementSettings(newPlacementId.value),
      url: newPlacementUrl.value,
    },
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'New placement added successfully'
      snackbarColor.value = 'success'
    })
    .catch(err => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to add new placement'
      snackbarColor.value = 'error'
    })
    .finally(() => {
      selectedPlacementId.value = newPlacementId.value
      selectedPlacementFriendlyName.value = formatPlacementIdToFriendlyName(
        newPlacementId.value,
      )
      closeAddPlacementDialog()
    })
}

const placements = computed(() => productSettings.placements)

const placementIdError = computed(() => {
  if (!newPlacementId.value) {
    return ''
  }

  if (productSettings.placements[newPlacementId.value]) {
    return 'This placement ID already exists'
  }

  if (!/^[a-z\d_-]+$/i.test(newPlacementId.value)) {
    return 'Placement ID can only contain letters, numbers, underscores, and hyphens'
  }

  return ''
})

const handleShowCompareDates = () => {
  previousStartDate.value = getFirstDayOfLastMonthAsDate()
  previousEndDate.value = getLastDayOfLastMonthAsDate()
  showCompareDates.value = !showCompareDates.value
}

const handleHideCompareDates = () => {
  previousStartDate.value = null
  previousEndDate.value = null
  showCompareDates.value = !showCompareDates.value
}

const removePlacementFilter = (placement: string) => {
  const index = filteredPlacements.value.indexOf(placement)
  if (index > -1) {
    filteredPlacements.value.splice(index, 1)
  }
}

const selectAllPlacements = () => {
  filteredPlacements.value = [...availablePlacements.value]
}

const clearAllPlacements = () => {
  filteredPlacements.value = []
}

const selectAllDeviceTypes = () => {
  filteredDeviceTypes.value = ['mobile', 'desktop', 'tablet', 'unknown']
}

const clearAllDeviceTypes = () => {
  filteredDeviceTypes.value = []
}

const removeDeviceTypeFilter = (deviceType: string) => {
  const index = filteredDeviceTypes.value.indexOf(deviceType)
  if (index > -1) {
    // Don't allow removing if it's the last remaining device type
    if (filteredDeviceTypes.value.length === 1) {
      return
    }
    filteredDeviceTypes.value.splice(index, 1)
  }
}

const toggleDeviceType = (deviceType: string) => {
  const currentIndex = filteredDeviceTypes.value.indexOf(deviceType)
  if (currentIndex > -1) {
    // Don't allow deselecting if it's the last remaining device type
    if (filteredDeviceTypes.value.length === 1) {
      return
    }
    // Remove device type if already selected
    filteredDeviceTypes.value.splice(currentIndex, 1)
  } else {
    // Add device type if not selected
    filteredDeviceTypes.value.push(deviceType)
  }
}

watch(
  [startDate, endDate],
  ([newStartDate, newEndDate]) => {
    if (newStartDate > newEndDate) {
      // Create a new Date object to ensure reactivity
      endDate.value = new Date(newStartDate)
    }

    // Update previous end date
    const dayBeforeStart = new Date(newStartDate)
    dayBeforeStart.setDate(dayBeforeStart.getDate() - 1)
    previousEndDate.value = new Date(dayBeforeStart)
  },
  { deep: true },
)

watch(
  availablePlacements,
  newPlacements => {
    filteredPlacements.value = [...newPlacements]
  },
  { immediate: true },
)
</script>

<style scoped>
/* Mobile-first responsive layout */
.filters-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

/* Desktop enhancement */
@media (min-width: 960px) {
  .filters-container {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
}

/* Navigation tabs - always show text, mobile-first */
.v-tab {
  min-width: auto;
}

/* Ensure proper touch targets on mobile */
@media (max-width: 959px) {
  .v-tab {
    padding: 12px 16px;
  }

  .filters-container {
    gap: 16px;
  }
}
</style>
